import {
  skuPackDescription,
  skuProductsPerUnit,
  skuUnitNoun,
} from '../../utils/sku';
import { SkuCardValues } from './types';

export type PalletProductsSuggestion = {
  products: number;
  unitsPerPallet: number;
  productsPerUnit: number;
  sourceLabel: string;
  sourceUnitNoun: string;
};

const suggestionFromCard = (
  card: SkuCardValues,
  cardIndex: number
): PalletProductsSuggestion | null => {
  const unit = card?.unitOfMeasure?.value;
  if (!unit || unit === 'Pallet') return null;

  const productsPerUnit = Number(card.amountOfProductInSku);
  const firstPalletConfig = card.palletising?.[0];
  const unitsPerLayer = Number(firstPalletConfig?.amountOfSkusInLayer);
  const layers = Number(firstPalletConfig?.amountOfLayersInPallet);
  if (!Number.isFinite(productsPerUnit) || productsPerUnit < 1) return null;
  if (!Number.isFinite(unitsPerLayer) || unitsPerLayer < 1) return null;
  if (!Number.isFinite(layers) || layers < 1) return null;

  const unitsPerPallet = unitsPerLayer * layers;
  return {
    products: unitsPerPallet * productsPerUnit,
    unitsPerPallet,
    productsPerUnit,
    sourceLabel: card.skuId || card.name || `SKU ${cardIndex + 1}`,
    sourceUnitNoun: skuUnitNoun({ unitOfMeasure: unit }),
  };
};

export const derivePalletProductsSuggestion = (
  cards: SkuCardValues[],
  palletCardIndex: number
): PalletProductsSuggestion | null => {
  const suggestions = cards
    .map((card, cardIndex) =>
      cardIndex === palletCardIndex ? null : suggestionFromCard(card, cardIndex)
    )
    .filter(
      (suggestion): suggestion is PalletProductsSuggestion =>
        suggestion !== null
    );
  return suggestions[0] ?? null;
};

const MAX_CHAIN_LINKS_SHOWN = 4;

export const buildSkuChainSummary = (cards: SkuCardValues[]): string | null => {
  const packSizes = cards
    .filter((card) => card?.unitOfMeasure?.value)
    .map((card) => ({
      factor: skuProductsPerUnit({
        amountOfProductInSku: Number(card.amountOfProductInSku),
      }),
      description: skuPackDescription({
        unitOfMeasure: card.unitOfMeasure.value,
        amountOfProductInSku: Number(card.amountOfProductInSku) || null,
      }),
    }))
    .sort((first, second) => first.factor - second.factor)
    .map((packSize) => packSize.description)
    .filter(
      (description, position, all) => all.indexOf(description) === position
    );

  if (packSizes.length < 2) return null;
  if (packSizes.length <= MAX_CHAIN_LINKS_SHOWN) return packSizes.join(' → ');

  const shown = packSizes.slice(0, MAX_CHAIN_LINKS_SHOWN - 1);
  return `${shown.join(' → ')} → +${packSizes.length - shown.length} more`;
};
